import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  VpnKey as TotpIcon,
} from '@mui/icons-material';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  // TOTP state
  const [requiresTOTP, setRequiresTOTP] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setWarning('');
    setLoading(true);

    if (requiresTOTP) {
      // Step 2: Verify TOTP
      const result = await login(null, null, totpCode, tempToken, rememberDevice);

      if (result.success) {
        navigate('/');
      } else {
        setError(result.error);
      }
    } else {
      // Step 1: Email + Password
      const result = await login(email, password);

      if (result.success) {
        navigate('/');
      } else if (result.requiresTOTP) {
        setRequiresTOTP(true);
        setTempToken(result.tempToken);
      } else {
        setError(result.error);
        if (result.warning) {
          setWarning(result.warning);
        }
      }
    }

    setLoading(false);
  };

  const handleBackToLogin = () => {
    setRequiresTOTP(false);
    setTempToken('');
    setTotpCode('');
    setError('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f1f5f9',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          borderRadius: 2,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <AdminIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" fontWeight={600}>
            {requiresTOTP ? 'Verify Identity' : 'Admin Login'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {requiresTOTP
              ? 'Enter your authenticator code or a backup code'
              : 'Second Clear Internal Dashboard'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {warning && !error.includes('Too many') && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {warning}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {requiresTOTP ? (
            <>
              <TextField
                fullWidth
                label="Verification Code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                margin="normal"
                required
                autoFocus
                placeholder="6-digit code or 8-char backup code"
                inputProps={{ maxLength: 8 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TotpIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Remember this device for 2 weeks
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !totpCode}
                sx={{ mt: 2, py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
              </Button>
              <Button
                fullWidth
                variant="text"
                size="small"
                onClick={handleBackToLogin}
                sx={{ mt: 1 }}
              >
                Back to login
              </Button>
            </>
          ) : (
            <>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
          This is an internal tool. Unauthorized access is prohibited.
        </Typography>
      </Paper>
    </Box>
  );
};

export default Login;
